import { useCallback, useMemo } from "react"

import {
  useTransportMutation,
  useTransportQuery,
  useTransportQueryUtils,
} from "@/transport"

import type { SavedViewResource } from "@/types_api/backend/transport/list_saved_views_response"

import type { SavedView, SavedViewConfig } from "../types/savedViews"

const UPLOADED_CONVERSATION_SAVED_VIEW_SCOPE_REQUEST = {
  scope: "monitoring",
} as const

function mapTransportSavedView(savedView: SavedViewResource): SavedView {
  return {
    config: (savedView.config ?? {}) as SavedViewConfig,
    name: savedView.name,
    ulid: savedView.ulid,
    userId: savedView.user_id,
  }
}

export function useConversationsSavedViewsTransport() {
  const transportQueryUtils = useTransportQueryUtils()

  const savedViewsQuery = useTransportQuery({
    operation: "listSavedViews",
    request: UPLOADED_CONVERSATION_SAVED_VIEW_SCOPE_REQUEST,
    options: {
      select: (data) => data.saved_views.map(mapTransportSavedView),
    },
  })

  const invalidateSavedViews = useCallback(
    () =>
      transportQueryUtils.invalidate(
        "listSavedViews",
        UPLOADED_CONVERSATION_SAVED_VIEW_SCOPE_REQUEST,
      ),
    [transportQueryUtils],
  )

  const createViewMutation = useTransportMutation("createSavedView", {
    onSuccess: () => {
      void invalidateSavedViews()
    },
  })
  const updateViewMutation = useTransportMutation("updateSavedView", {
    onSuccess: () => {
      void invalidateSavedViews()
    },
  })
  const deleteViewMutation = useTransportMutation("deleteSavedView", {
    onSuccess: () => {
      void invalidateSavedViews()
    },
  })

  return useMemo(
    () => ({
      createView: {
        ...createViewMutation,
        mutateAsync: (
          input: Omit<
            Parameters<typeof createViewMutation.mutateAsync>[0],
            "scope"
          >,
        ) =>
          createViewMutation.mutateAsync({
            ...UPLOADED_CONVERSATION_SAVED_VIEW_SCOPE_REQUEST,
            ...input,
          }),
      },
      deleteView: {
        ...deleteViewMutation,
        mutate: (
          input: Omit<Parameters<typeof deleteViewMutation.mutate>[0], "scope">,
          options?: Parameters<typeof deleteViewMutation.mutate>[1],
        ) =>
          deleteViewMutation.mutate(
            {
              ...UPLOADED_CONVERSATION_SAVED_VIEW_SCOPE_REQUEST,
              ...input,
            },
            options,
          ),
      },
      isLoadingViews: savedViewsQuery.isLoading,
      savedViews: savedViewsQuery.data,
      updateView: {
        ...updateViewMutation,
        mutateAsync: (
          input: Omit<
            Parameters<typeof updateViewMutation.mutateAsync>[0],
            "scope"
          >,
        ) =>
          updateViewMutation.mutateAsync({
            ...UPLOADED_CONVERSATION_SAVED_VIEW_SCOPE_REQUEST,
            ...input,
          }),
      },
    }),
    [
      createViewMutation,
      deleteViewMutation,
      savedViewsQuery.data,
      savedViewsQuery.isLoading,
      updateViewMutation,
    ],
  )
}
